import { useMutation } from '@apollo/client';
import type { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import MoveMoneyInnerForm from 'components/budget/MoveMoneyInnerForm';
import Form from 'components/lib/form/Form';
import EventPropagationBoundary from 'components/lib/higherOrder/EventPropagationBoundary';
import Card from 'components/lib/ui/Card';

import {
  convertDisplayBudgetToCalculableBudget,
  getFlexCategoryIdsFromDisplayBudget,
  getInitialFormValues,
  validateMoveMoneyForm,
  calculateMoveMoneyOptimisticUpdates,
  buildMoveMoneyMutationInput,
} from 'common/lib/budget/MoveMoney';
import { PLAN_DATA_QUERY_NAME } from 'common/lib/graphQl/planning';
import useBudgetData from 'common/lib/hooks/budget/useBudgetData';
import { useOptimisticBudgetUpdates } from 'common/lib/hooks/budget/useOptimisticBudgetUpdates';
import useToast from 'lib/hooks/useToast';

import { gql } from 'common/generated/gql';
import type { BudgetTimeframeInput, CategoryGroupType } from 'common/generated/graphql';
import type { ExtractInputFromDocument } from 'common/types/graphql';

const WIDTH_PX = 304;

const StyledCard = styled(Card)`
  width: ${WIDTH_PX}px;
`;

const CardBody = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
`;

const Title = styled.span`
  color: ${({ theme }) => theme.color.textLight};
`;

type Props = {
  budgetStartDate: DateTime;
  onDone: () => void;
  categoryType: CategoryGroupType;
  initialCategoryId?: string;
  isGroup: boolean;
};

const MoveMoneyPopover = ({
  budgetStartDate,
  onDone,
  categoryType,
  initialCategoryId,
  isGroup,
}: Props) => {
  const { openErrorToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { budgetListData, budgetSummaryData, isFlexBudgetSystem } = useBudgetData(
    budgetStartDate,
    {},
  );

  const budget = useMemo(
    () =>
      budgetListData && convertDisplayBudgetToCalculableBudget(budgetListData, isFlexBudgetSystem),
    [budgetListData, budgetSummaryData],
  );

  const excludedCategoryIds = useMemo(
    // if flex budgeting is enabled, all flex categories are excluded from the move money form
    () =>
      isFlexBudgetSystem
        ? budgetListData && getFlexCategoryIdsFromDisplayBudget(budgetListData)
        : [],
    [budgetListData, isFlexBudgetSystem],
  );

  const [mutate] = useMutation(MOVE_MONEY_MUTATION, {
    refetchQueries: [PLAN_DATA_QUERY_NAME],
    onCompleted: onDone,
  });

  const { optimisticUpdateCacheForCategoryOrGroup } = useOptimisticBudgetUpdates();

  const handleSubmit = useCallback(
    async (mutationInput: ExtractInputFromDocument<typeof MOVE_MONEY_MUTATION>) => {
      setIsSubmitting(true);

      const updates = calculateMoveMoneyOptimisticUpdates(
        mutationInput,
        budgetListData,
        categoryType,
      );

      if (updates) {
        optimisticUpdateCacheForCategoryOrGroup(updates.fromUpdate);
        optimisticUpdateCacheForCategoryOrGroup(updates.toUpdate);
      }

      await mutate({ variables: { input: mutationInput } })
        .catch(() => {
          openErrorToast();
        })
        .finally(() => setIsSubmitting(false));
    },
    [mutate, optimisticUpdateCacheForCategoryOrGroup, budgetListData, categoryType, openErrorToast],
  );

  const initialValues = useMemo(
    () =>
      initialCategoryId && budget
        ? {
            ...getInitialFormValues(
              budget,
              initialCategoryId,
              categoryType,
              isGroup,
              isFlexBudgetSystem,
            ),
            startDate: budgetListData?.startDate ?? undefined,
            timeframe: 'month' as BudgetTimeframeInput,
          }
        : {
            startDate: budgetListData?.startDate ?? undefined,
            timeframe: 'month' as BudgetTimeframeInput,
          },
    [budget, budgetListData, initialCategoryId, categoryType, isGroup, isFlexBudgetSystem],
  );

  return (
    <EventPropagationBoundary onClick>
      <StyledCard title={<Title>Move Budget</Title>} small>
        <CardBody>
          <Form
            onSubmit={handleSubmit}
            initialValues={initialValues}
            transformValuesBeforeSubmit={(values) => {
              const { fromCategoryId, toCategoryId } = values;
              return buildMoveMoneyMutationInput({
                timeframe: values.timeframe,
                amount: values.amount,
                startDate: values.startDate,
                fromCategoryId,
                toCategoryId,
                budget,
              });
            }}
            validate={(values) => (budget ? validateMoveMoneyForm(values, budget) : undefined)}
          >
            <MoveMoneyInnerForm
              budget={budget}
              categoryType={categoryType}
              isPending={isSubmitting}
              excludeCategories={excludedCategoryIds}
              isFlexBudgetSystem={isFlexBudgetSystem}
            />
          </Form>
        </CardBody>
      </StyledCard>
    </EventPropagationBoundary>
  );
};

const MOVE_MONEY_MUTATION = gql(/* GraphQL */ `
  mutation Web_MoveMoneyMutation($input: MoveMoneyMutationInput!) {
    moveMoneyBetweenCategories(input: $input) {
      fromBudgetItem {
        id
        budgetAmount
      }
      toBudgetItem {
        id
        budgetAmount
      }
    }
  }
`);

export default MoveMoneyPopover;
